import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store/hooks';
import { branding } from '../constants/branding';
import QRCode from 'qrcode';
import axiosInstance from '../services/axiosConfig';
import { web3Provider } from '../services/web3Provider';
import RetailPlatformAirdrops from '../components/crypto/RetailPlatformAirdrops';

interface Coin {
  id: number;
  name: string;
  symbol: string;
  icon: string;
  amount: string;
  value: number;
  price: number | null;
  change24h: string;
  color: string;
  history: number[];
}

type CryptoTransaction = {
  id: number;
  type: string;
  coin: string;
  symbol: string;
  amount: string;
  price: number | null;
  total: number | null;
  date: string;
  status: string;
  icon: string;
};

function formatUsdAmount(value: unknown): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}

function formatUsdCell(value: unknown): string {
  const s = formatUsdAmount(value);
  return s === '—' ? '—' : `$${s}`;
}

interface WalletDetails {
  blockchain: string;
  address: string;
  publicKey: string;
  createdAt: string;
  balance: number;
  id?: number; // Added id for private key fetching
}

interface BlockchainTransaction {
  transactionHash: string;
  blockchain: string;
  fromAddress: string;
  toAddress: string;
  amount: number;
  fee: number;
  status: string;
  timestamp: string;
  confirmations: number;
  tokenSymbol: string;
  blockNumber: string;
  network: string;
}

const spinnerStyle = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Crypto: React.FC = () => {
  // Get token from Redux store instead of localStorage
  const token = useAppSelector(state => state.auth.token);
  
  const [selectedTimeframe, setSelectedTimeframe] = useState('24h');
  const [page, setPage] = useState(0);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState<Coin | null>(null);
  const [amount, setAmount] = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  const [walletAddress, setWalletAddress] = useState('bc1q...');
  const [isProcessing, setIsProcessing] = useState(false);
  const pageSize = 5;
  const navigate = useNavigate();

  const MOBILE_BREAKPOINT = 768;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);

  // Add state for fetched crypto portfolio
  const [portfolio, setPortfolio] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [wallets, setWallets] = useState<WalletDetails[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [showCreateWalletModal, setShowCreateWalletModal] = useState(false);
  const [selectedBlockchain, setSelectedBlockchain] = useState('');
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  
  // Add state for crypto prices
  const [cryptoPrices, setCryptoPrices] = useState<{[key: string]: number}>({});
  const [cryptoPricesLoading, setCryptoPricesLoading] = useState(true);

  // Add state for wallet import
  const [showImportWalletModal, setShowImportWalletModal] = useState(false);
  const [importMethod, setImportMethod] = useState<'privateKey' | 'recoveryPhrase'>('privateKey');
  const [importPrivateKey, setImportPrivateKey] = useState('');
  const [importRecoveryPhrase, setImportRecoveryPhrase] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importWalletName, setImportWalletName] = useState('');
  const [isImportingWallet, setIsImportingWallet] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [selectedImportBlockchain, setSelectedImportBlockchain] = useState('');

  const SUPPORTED_BLOCKCHAINS = ['Bitcoin', 'Ethereum', 'Polygon', 'Stellar', 'Solana', 'Cardano'];

  const [selectedWallet, setSelectedWallet] = useState<WalletDetails | null>(null);
  const [blockchainTransactions, setBlockchainTransactions] = useState<BlockchainTransaction[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [qrCodes, setQrCodes] = useState<{[key: string]: string}>({});
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);

  // Add state for address search
  const [searchAddress, setSearchAddress] = useState('');
  const [searchBlockchain, setSearchBlockchain] = useState('Bitcoin');
  const [searchResults, setSearchResults] = useState<BlockchainTransaction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Add state for search mode and hash
  const [searchMode, setSearchMode] = useState<'address' | 'hash'>('address');
  const [searchHash, setSearchHash] = useState('');

  const [expandedTxIdx, setExpandedTxIdx] = useState<number | null>(null);

  // Add state for private key modal
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);
  const [selectedWalletForPrivateKey, setSelectedWalletForPrivateKey] = useState<WalletDetails | null>(null);
  const [privateKey, setPrivateKey] = useState<string>('');
  const [isLoadingPrivateKey, setIsLoadingPrivateKey] = useState(false);
  const [privateKeyError, setPrivateKeyError] = useState<string | null>(null);
  const [privateKeyVisible, setPrivateKeyVisible] = useState(false);
  const [privateKeyTimeout, setPrivateKeyTimeout] = useState<NodeJS.Timeout | null>(null);
  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);
  const [networkStatus, setNetworkStatus] = useState<{ useTestnet: boolean; networkNames: Record<string, string> } | null>(null);

  // Add state for asset-backed tokens
  const [assetTokens, setAssetTokens] = useState<any[]>([]);
  const [showAssetTokenModal, setShowAssetTokenModal] = useState(false);
  const [selectedAssetToken, setSelectedAssetToken] = useState<any>(null);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [isInvesting, setIsInvesting] = useState(false);
  const [investmentError, setInvestmentError] = useState<string | null>(null);
  
  // Add state for user investments
  const [userInvestments, setUserInvestments] = useState<any[]>([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  
  // Add ref for investment input
  const investmentInputRef = useRef<HTMLInputElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Investment tracking state
  const [showInvestmentDetailsModal, setShowInvestmentDetailsModal] = useState(false);
  const [selectedInvestment, setSelectedInvestment] = useState<any>(null);

  // Debounced investment amount handler
  const handleInvestmentAmountChange = useCallback((value: string) => {
    setInvestmentAmount(value);
  }, []);

  // Focus investment input when modal opens
  useEffect(() => {
    if (showAssetTokenModal && investmentInputRef.current) {
      setTimeout(() => {
        investmentInputRef.current?.focus();
      }, 100);
    }
  }, [showAssetTokenModal]);

  // Helper function to get crypto symbol for each blockchain
  const getCryptoSymbol = (blockchain: string): string => {
    switch (blockchain.toLowerCase()) {
      case 'ethereum':
        return 'ETH';
      case 'polygon':
        return 'MATIC';
      case 'bitcoin':
        return 'BTC';
      case 'stellar':
        return 'XLM';
      case 'solana':
        return 'SOL';
      case 'cardano':
        return 'ADA';
      default:
        return blockchain.toUpperCase();
    }
  };

  // Function to fetch crypto prices
  const fetchCryptoPrices = async () => {
    setCryptoPricesLoading(true);
    try {
      // Using CoinGecko API for real-time prices
      const response = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum,bitcoin,matic-network,stellar,solana,cardano&vs_currencies=usd');
      const data = await response.json();
      
      setCryptoPrices({
        'ethereum': data.ethereum?.usd || 0,
        'bitcoin': data.bitcoin?.usd || 0,
        'polygon': data['matic-network']?.usd || 0,
        'stellar': data.stellar?.usd || 0,
        'solana': data.solana?.usd || 0,
        'cardano': data.cardano?.usd || 0
      });
    } catch (error) {
      console.error('Failed to fetch crypto prices:', error);
      // Set fallback prices
      setCryptoPrices({
        'ethereum': 4583.47, // Current ETH price from your screenshot
        'bitcoin': 50000,
        'polygon': 1.5,
        'stellar': 0.5,
        'solana': 100,
        'cardano': 2.0
      });
    } finally {
      setCryptoPricesLoading(false);
    }
  };

  // Function to calculate USD value from crypto balance
  const getUsdValue = (blockchain: string, balance: number): number => {
    const price = cryptoPrices[blockchain.toLowerCase()];
    if (price === undefined) {
      // Prices not loaded yet, return 0 to avoid showing incorrect values
      return 0;
    }
    return balance * price;
  };

  // Helper function to get network status from backend data
  const getNetworkStatus = (blockchain: string): { isTestnet: boolean; networkName: string } => {
    if (!networkStatus) {
      return { isTestnet: false, networkName: 'Mainnet' }; // Default fallback
    }
    
    const isTestnet = networkStatus.useTestnet;
    const networkName = networkStatus.networkNames[blockchain.toLowerCase()] || 
                       (isTestnet ? 'Testnet' : 'Mainnet');
    
    return { isTestnet, networkName };
  };

  // Function to fetch network status from backend
  const fetchNetworkStatus = async () => {
    try {
      const response = await axiosInstance.get('/crypto/network-status');
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setNetworkStatus(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching network status:', error);
    }
  };

  // Function to fetch asset-backed tokens
  const fetchAssetTokens = async () => {
    try {
      const response = await axiosInstance.get('/retail/asset-tokens/active');
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setAssetTokens(data.data || []);
        } else {
          console.error('Failed to fetch asset tokens:', data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching asset tokens:', error);
    }
  };

  // Function to fetch user investments
  const fetchUserInvestments = async () => {
    try {
      setLoadingInvestments(true);
      const response = await axiosInstance.get('/retail/asset-tokens/my-investments');
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setUserInvestments(data.data || []);
        } else {
          console.error('Failed to fetch user investments:', data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching user investments:', error);
    } finally {
      setLoadingInvestments(false);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Add the spinner animation styles to the document
    const styleElement = document.createElement('style');
    styleElement.innerHTML = spinnerStyle;
    document.head.appendChild(styleElement);

    return () => {
      // Clean up the styles when component unmounts
      document.head.removeChild(styleElement);
    };
  }, []);

  // Prevent body scrolling when any modal is open
  useEffect(() => {
    const hasOpenModal = showBuyModal || showSendModal || showReceiveModal || 
                         showCreateWalletModal || showImportWalletModal || showAssetTokenModal;
    
    if (hasOpenModal) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = scrollPositionRef.current;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY !== undefined) {
        window.scrollTo(0, scrollY);
        scrollPositionRef.current = 0;
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showBuyModal, showSendModal, showReceiveModal, showCreateWalletModal, showImportWalletModal, showAssetTokenModal]);

  // Add fetchPortfolio function inside the component
  const fetchPortfolio = async () => {
    try {
      const response = await axiosInstance.get('/crypto/portfolio');
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          setPortfolio(data.data);
        } else {
          console.error('Failed to fetch portfolio:', data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
    }
  };

  // Add fetchWallets function
  const fetchWallets = async () => {
    try {
      setWalletsLoading(true);
      const response = await axiosInstance.get('/crypto/wallet');
      
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          setWallets(data.data || []);
        } else {
          console.error('Failed to fetch wallets:', data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setWalletsLoading(false);
    }
  };

  // Add createWallet function
  const handleCreateWallet = async () => {
    if (!selectedBlockchain || !token) return;
    
    setIsCreatingWallet(true);
    try {
      const response = await axiosInstance.post('/crypto/wallet/create', {
          blockchain: selectedBlockchain,
      });

      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          await fetchWallets();
          setShowCreateWalletModal(false);
          setSelectedBlockchain('');
        } else {
          console.error('Failed to create wallet:', data.message);
        }
      }
    } catch (error) {
      console.error('Error creating wallet:', error);
    } finally {
      setIsCreatingWallet(false);
    }
  };

  // Add importWallet function
  const handleImportWallet = async () => {
    if (!selectedImportBlockchain || !token) return;
    
    // Validate input based on import method
    if (importMethod === 'privateKey' && !importPrivateKey.trim()) {
      setImportError('Private key is required');
      return;
    }
    if (importMethod === 'recoveryPhrase' && !importRecoveryPhrase.trim()) {
      setImportError('Recovery phrase is required');
      return;
    }
    
    setIsImportingWallet(true);
    setImportError(null);
    
    try {
      const requestBody: any = {
        blockchain: selectedImportBlockchain,
      };
      
      if (importMethod === 'privateKey') {
        requestBody.privateKey = importPrivateKey.trim();
      } else {
        requestBody.recoveryPhrase = importRecoveryPhrase.trim();
        if (importPassword.trim()) {
          requestBody.password = importPassword.trim();
        }
      }
      
      if (importWalletName.trim()) {
        requestBody.walletName = importWalletName.trim();
      }

      const response = await axiosInstance.post('/crypto/wallet/import', requestBody);

      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          await fetchWallets();
          setShowImportWalletModal(false);
          // Reset form
          setImportMethod('privateKey');
          setImportPrivateKey('');
          setImportRecoveryPhrase('');
          setImportPassword('');
          setImportWalletName('');
          setSelectedImportBlockchain('');
          setImportError(null);
        } else {
          setImportError(data.message || 'Failed to import wallet');
        }
      }
    } catch (error) {
      console.error('Error importing wallet:', error);
      let errorMsg = 'Error importing wallet.';
      const err = error as any;
      if (err && typeof err === 'object') {
        if (err.response && typeof err.response === 'object') {
          const errData = err.response.data;
          if (errData && errData.message) {
            errorMsg = errData.message;
          }
        } else if (err.message && typeof err.message === 'string') {
          errorMsg = err.message;
        }
      }
      setImportError(errorMsg);
    } finally {
      setIsImportingWallet(false);
    }
  };

  // Function to invest in asset-backed tokens
  const handleInvestInAssetToken = async () => {
    if (!selectedAssetToken || !investmentAmount) return;
    
    const amount = parseFloat(investmentAmount);
    if (amount < selectedAssetToken.minimumInvestment) {
      setInvestmentError(`Minimum investment is $${selectedAssetToken.minimumInvestment}`);
      return;
    }
    
    setIsInvesting(true);
    setInvestmentError(null);
    
    try {
      const response = await axiosInstance.post('/retail/asset-tokens/invest', {
        assetTokenId: selectedAssetToken.id,
        investmentAmount: amount,
        transactionHash: null // Optional transaction hash
      });
      
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          // alert('Investment successful! Your tokens have been added to your portfolio.');
          setShowAssetTokenModal(false);
          setSelectedAssetToken(null);
          setInvestmentAmount('');
          setInvestmentError(null);
          
          // Refresh user investments to show the new investment
          await fetchUserInvestments();
        } else {
          setInvestmentError(data.message || 'Failed to invest');
        }
      } else {
        setInvestmentError('Failed to process investment');
      }
    } catch (error: any) {
      console.error('Investment error:', error);
      setInvestmentError(error.response?.data?.message || 'Failed to invest');
    } finally {
      setIsInvesting(false);
    }
  };

  // Add fetchBlockchainTransactions function
  const fetchBlockchainTransactions = async (blockchain: string, walletAddress: string) => {
    if (!token) {
      setError('No authentication token available');
      return;
    }
    
    setIsLoadingTransactions(true);
    try {
      const response = await axiosInstance.get(`/crypto/transactions/${blockchain}?walletAddress=${walletAddress}`);
      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          setBlockchainTransactions(data.data || []);
        } else {
          console.error('Failed to fetch blockchain transactions:', data.message);
        }
      }
    } catch (error) {
      console.error('Error fetching blockchain transactions:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  // Add function to fetch private key
  const fetchPrivateKey = async (wallet: WalletDetails) => {
    if (!token || !wallet.id) {
      setPrivateKeyError('No authentication token available or invalid wallet');
      return;
    }
    
    setIsLoadingPrivateKey(true);
    setPrivateKeyError(null);
    setPrivateKey('');
    setPrivateKeyVisible(false);
    
    try {
      const response = await axiosInstance.get(`/crypto/wallet/${wallet.id}/private-key`);
      if (response.status === 200) {
        const data = response.data;
        if (data.success || data.status === 'success') {
          setPrivateKey(data.data.privateKey);
          setPrivateKeyVisible(true);
          
          // Auto-hide private key after 30 seconds
          if (privateKeyTimeout) {
            clearTimeout(privateKeyTimeout);
          }
          const timeout = setTimeout(() => {
            setPrivateKeyVisible(false);
            setPrivateKey('');
          }, 30000); // 30 seconds
          setPrivateKeyTimeout(timeout);
        } else {
          setPrivateKeyError(data.message || 'Failed to retrieve private key');
        }
      }
    } catch (error) {
      console.error('Error fetching private key:', error);
      let errorMsg = 'Error retrieving private key.';
      const err = error as any;
      if (err && typeof err === 'object') {
        if (err.response && typeof err.response === 'object') {
          const errData = err.response.data;
          if (errData && errData.message) {
            errorMsg = errData.message;
          }
        } else if (err.message && typeof err.message === 'string') {
          errorMsg = err.message;
        }
      }
      setPrivateKeyError(errorMsg);
    } finally {
      setIsLoadingPrivateKey(false);
    }
  };

  // Add function to show private key modal
  const handleShowPrivateKey = (wallet: WalletDetails, event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedWalletForPrivateKey(wallet);
    
    // Get the wallet card element (parent of the button)
    const walletCard = event.currentTarget.closest('[data-wallet-card]') as HTMLElement;
    if (walletCard) {
      const rect = walletCard.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      
      // Position modal over the wallet card
      setModalPosition({
        top: rect.top + scrollTop,
        left: rect.left + scrollLeft
      });
    } else {
      setModalPosition(null); // Fallback to center if card not found
    }
    
    setShowPrivateKeyModal(true);
    fetchPrivateKey(wallet);
  };

  // Add function to close private key modal
  const handleClosePrivateKeyModal = () => {
    setShowPrivateKeyModal(false);
    setSelectedWalletForPrivateKey(null);
    setPrivateKey('');
    setPrivateKeyError(null);
    setPrivateKeyVisible(false);
    setModalPosition(null);
    if (privateKeyTimeout) {
      clearTimeout(privateKeyTimeout);
      setPrivateKeyTimeout(null);
    }
  };

  // Add wallet selection handler
  const handleWalletSelect = (wallet: WalletDetails) => {
    setSelectedWallet(wallet);
    fetchBlockchainTransactions(wallet.blockchain, wallet.address);
  };

  useEffect(() => {
    fetchPortfolio();
    fetchWallets();
    fetchNetworkStatus(); // Fetch network status on mount
    fetchAssetTokens(); // Fetch asset tokens on mount
    fetchUserInvestments(); // Fetch user investments on mount
    fetchCryptoPrices(); // Fetch crypto prices on mount (async, won't block wallet display)
  }, []);

  // Cleanup body scroll on unmount (removed the scroll lock that was causing freezing)
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Use fetched portfolio or calculate from coins
  const cryptoPortfolio = portfolio || {
    coins: [],
    transactions: []
  };

  // Use cryptoTransactions from portfolio or empty array
  const cryptoTransactions = cryptoPortfolio.transactions || [];

  // Calculate total portfolio value and changes
  const calculatePortfolioTotals = () => {
    // Calculate value from mock coins (if any)
    const coinsValue = (cryptoPortfolio.coins || []).reduce((sum: number, coin: Coin): number => {
      const unit = Number(coin.price);
      const line = parseFloat(coin.amount) * (Number.isFinite(unit) ? unit : 0);
      return sum + line;
    }, 0);

    // Calculate value from real crypto wallets
    const walletsValue = wallets.reduce((sum: number, wallet: WalletDetails): number => {
      return sum + getUsdValue(wallet.blockchain, wallet.balance || 0);
    }, 0);

    // Total value is sum of both
    const totalValue = coinsValue + walletsValue;

    // For now, use a default 24h change since we don't have historical data for wallets
    // In a real implementation, you'd track price changes over time
    const totalChangePercent = 0; // Default to 0% change
    const totalChangeValue = 0; // Default to $0 change

    return {
      totalValue: totalValue,
      change24h: totalChangePercent,
      totalChange24h: `${totalChangeValue >= 0 ? '+' : ''}${totalChangePercent.toFixed(1)}%`,
      totalChangeValue24h: `${totalChangeValue >= 0 ? '+' : ''}$${Math.abs(totalChangeValue).toFixed(2)}`
    };
  };

  const portfolioTotals = calculatePortfolioTotals();

  // Update the select onChange handlers with proper types
  const handleCoinSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const coin = cryptoPortfolio.coins.find((c: Coin) => c.symbol === e.target.value);
    setSelectedCoin(coin || null);
  };

  const handleBuyCrypto = async () => {
    if (!selectedCoin || !amount || !token) return;
    
    setIsProcessing(true);
    try {
      const response = await axiosInstance.post('/crypto/buy', {
          coinSymbol: selectedCoin?.symbol,
          amount: parseFloat(amount),
          price: selectedCoin?.price,
      });

      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          await fetchPortfolio();
          setShowBuyModal(false);
          setAmount('');
          setSelectedCoin(null);
        } else {
          console.error('Failed to buy crypto:', data.message);
        }
      }
    } catch (error) {
      console.error('Error buying crypto:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendCrypto = async () => {
    if (!selectedCoin || !amount || !recipientAddress || !token) return;
    
    setIsProcessing(true);
    try {
      const response = await axiosInstance.post('/crypto/send', {
          coinSymbol: selectedCoin?.symbol,
          amount: parseFloat(amount),
          recipientAddress,
      });

      if (response.status === 200) {
        const data = response.data;
        // Check for both possible response formats: data.success or data.status === 'success'
        if (data.success || data.status === 'success') {
          await fetchPortfolio();
          setShowSendModal(false);
          setAmount('');
          setRecipientAddress('');
          setSelectedCoin(null);
        } else {
          console.error('Failed to send crypto:', data.message);
        }
      }
    } catch (error) {
      console.error('Error sending crypto:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate QR code for wallet address
  const generateQRCode = async (address: string) => {
    try {
      if (qrCodes[address]) return qrCodes[address]; // Return cached QR code
      
      const qrCodeDataURL = await QRCode.toDataURL(address, {
        width: isMobile ? 120 : 150,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodes(prev => ({
        ...prev,
        [address]: qrCodeDataURL
      }));
      
      return qrCodeDataURL;
    } catch (error) {
      console.error('Error generating QR code:', error);
      return null;
    }
  };

  // Generate QR codes for all wallets when wallets are loaded
  useEffect(() => {
    if (wallets.length > 0) {
      wallets.forEach(wallet => {
        generateQRCode(wallet.address);
      });
    }
  }, [wallets, isMobile]);

  // Function to copy address with feedback
  const copyAddressToClipboard = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 2000); // Clear after 2 seconds
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  // Update WalletSection to include transaction history
  const WalletSection = () => (
    <div style={{
      ...branding.cryptoPage.wallets.card,
      padding: isMobile ? branding.cryptoPage.wallets.card.padding.mobile : branding.cryptoPage.wallets.card.padding.desktop
    } as React.CSSProperties}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 12,
      }}>
        <h3 style={{
          margin: branding.cryptoPage.wallets.header.title.margin,
          color: branding.cryptoPage.wallets.header.title.color,
          fontSize: isMobile ? branding.cryptoPage.wallets.header.title.fontSize.mobile : branding.cryptoPage.wallets.header.title.fontSize.desktop,
          fontWeight: branding.cryptoPage.wallets.header.title.fontWeight,
        }}>Your Crypto Wallets</h3>
        <div style={{
          display: 'flex',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 8 : 12,
          width: isMobile ? '100%' : 'auto',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => setShowBuyModal(true)}
            style={{
              padding: isMobile ? '8px 12px' : '10px 20px',
              background: '#9c27b0',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              flex: isMobile ? '0 0 calc(50% - 4px)' : 'auto',
              minWidth: isMobile ? 0 : 'auto',
              height: '40px',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>💰</span> <span>Buy Crypto</span>
          </button>
          <button
            onClick={() => setShowSendModal(true)}
            style={{
              padding: isMobile ? '8px 12px' : '10px 20px',
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              flex: isMobile ? '0 0 calc(50% - 4px)' : 'auto',
              minWidth: isMobile ? 0 : 'auto',
              height: '40px',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>📤</span> <span>Send Crypto</span>
          </button>
        <button
          onClick={() => setShowCreateWalletModal(true)}
          style={{
              padding: isMobile ? '8px 12px' : '10px 20px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              flex: isMobile ? '0 0 calc(50% - 4px)' : 'auto',
              minWidth: isMobile ? 0 : 'auto',
              height: '40px',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>➕</span> <span>Create Wallet</span>
        </button>
        <button
          onClick={() => setShowImportWalletModal(true)}
          style={{
              padding: isMobile ? '8px 12px' : '10px 20px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '4px' : '8px',
              flex: isMobile ? '0 0 calc(50% - 4px)' : 'auto',
              minWidth: isMobile ? 0 : 'auto',
              height: '40px',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>📥</span> <span>Import Wallet</span>
        </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: 16,
      }}>
        {walletsLoading ? (
          <div style={{
            gridColumn: '1 / -1',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px',
            flexDirection: 'column',
            gap: '16px',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
            <div style={{
              color: '#666',
              fontSize: '16px',
              fontWeight: '500',
            }}>
              Loading crypto wallets...
            </div>
          </div>
        ) : wallets.length === 0 ? (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '40px',
            color: '#666',
            fontSize: '16px',
          }}>
            No crypto wallets found. Create your first wallet to get started!
          </div>
        ) : (
          (wallets || []).map((wallet, index) => (
          <div
            key={index}
            data-wallet-card
            style={{
              ...branding.cryptoPage.wallets.walletCard,
              padding: isMobile ? branding.cryptoPage.layout.paddingBottom.mobile : branding.cryptoPage.layout.paddingBottom.desktop
            } as React.CSSProperties}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 12,
              gap: 8,
            }}>
              <span style={branding.cryptoPage.wallets.blockchainTag}>
                {wallet.blockchain}
              </span>
            </div>
            
            {/* Responsive layout for address and QR code */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 12 : 16,
              alignItems: isMobile ? 'stretch' : 'flex-start',
            }}>
              {/* Address section */}
              <div style={{ flex: 1 }}>
                <div style={{
                  ...branding.cryptoPage.wallets.addressBox,
              wordBreak: 'break-all',
            }}>
              {wallet.address}
            </div>
            <div style={{
              fontSize: 12,
                  color: branding.cryptoPage.header.subtitle.color,
            }}>
              Created: {new Date(wallet.createdAt).toLocaleDateString()}
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#333',
                  marginTop: 4,
                  fontWeight: 500
                }}>
                  Balance: {cryptoPricesLoading ? (
                    <span style={{ color: '#007bff' }}>Loading...</span>
                  ) : (
                    <>${getUsdValue(wallet.blockchain, wallet.balance || 0).toFixed(2)} {getCryptoSymbol(wallet.blockchain)} {wallet.balance?.toFixed(6) || '0.000000'}</>
                  )}
                </div>
                
                {/* Network Status Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '4px',
                }}>
                  <div style={{
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    backgroundColor: getNetworkStatus(wallet.blockchain).isTestnet ? '#fef3c7' : '#dcfce7',
                    color: getNetworkStatus(wallet.blockchain).isTestnet ? '#92400e' : '#166534',
                    border: `1px solid ${getNetworkStatus(wallet.blockchain).isTestnet ? '#fde68a' : '#bbf7d0'}`,
                  }}>
                    {getNetworkStatus(wallet.blockchain).networkName}
                  </div>
                </div>
              </div>
              
              {/* QR Code section */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMobile ? 'center' : 'flex-end',
                gap: 8,
              }}>
                {qrCodes[wallet.address] ? (
                  <div style={{
                    ...branding.cryptoPage.wallets.qrCode.container,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <img
                      src={qrCodes[wallet.address]}
                      alt={`QR Code for ${wallet.address}`}
                      style={{
                        width: isMobile ? branding.cryptoPage.wallets.qrCode.size.mobile : branding.cryptoPage.wallets.qrCode.size.desktop,
                        height: isMobile ? branding.cryptoPage.wallets.qrCode.size.mobile : branding.cryptoPage.wallets.qrCode.size.desktop,
                        borderRadius: 4,
                      }}
                    />
                    <div style={{
                      fontSize: branding.cryptoPage.wallets.qrCode.helperText.fontSize,
                      color: branding.cryptoPage.wallets.qrCode.helperText.color,
                      textAlign: 'center',
                      fontWeight: branding.cryptoPage.wallets.qrCode.helperText.fontWeight,
                    }}>
                      Scan to copy address
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: isMobile ? branding.cryptoPage.wallets.qrCode.size.mobile : branding.cryptoPage.wallets.qrCode.size.desktop,
                    height: isMobile ? branding.cryptoPage.wallets.qrCode.size.mobile : branding.cryptoPage.wallets.qrCode.size.desktop,
                    background: '#f0f0f0',
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: '#999',
                  }}>
                    Generating QR...
                  </div>
                )}
                
                {/* Copy address button */}
                <button
                  onClick={() => copyAddressToClipboard(wallet.address)}
                  style={{
                    ...branding.cryptoPage.wallets.copyButton,
                    ...(copiedAddress === wallet.address 
                      ? branding.cryptoPage.wallets.copyButton.copied 
                      : branding.cryptoPage.wallets.copyButton.normal),
                  }}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    if (copiedAddress !== wallet.address) {
                      target.style.background = branding.cryptoPage.wallets.copyButton.hover.background;
                    }
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    if (copiedAddress !== wallet.address) {
                      target.style.background = branding.cryptoPage.wallets.copyButton.normal.background;
                    }
                  }}
                >
                  {copiedAddress === wallet.address ? '✅ Copied!' : '📋 Copy'}
                </button>

                {/* Show Private Key button */}
                <button
                  onClick={(e) => handleShowPrivateKey(wallet, e)}
                  style={{
                    padding: '8px 12px',
                    background: '#dc3545',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    width: '100%',
                    justifyContent: 'center',
                    transition: 'background-color 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#c82333';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#dc3545';
                  }}
                >
                  <span>🔑</span> Show Private Key
                </button>
              </div>
            </div>
          </div>
        ))
        )}
      </div>

      {/* Transaction History */}
      <div style={{ marginTop: 32 }}>
        <h3 style={{
          margin: '0 0 16px 0',
          color: branding.textColor,
          fontSize: isMobile ? branding.cryptoPage.wallets.header.title.fontSize.mobile : branding.cryptoPage.wallets.header.title.fontSize.desktop,
          fontWeight: branding.cryptoPage.wallets.header.title.fontWeight,
        }}>Blockchain Transactions</h3>

        {/* Address Search Bar */}
        <div style={{
          padding: '16px',
          background: '#f9f9f9',
          borderRadius: 8,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <select
            value={searchBlockchain}
            onChange={e => setSearchBlockchain(e.target.value)}
            style={{ padding: 8, borderRadius: 4 }}
          >
            <option value="Bitcoin">Bitcoin</option>
            <option value="Ethereum">Ethereum</option>
            <option value="Polygon">Polygon</option>
            <option value="Stellar">Stellar</option>
            <option value="Cardano">Cardano</option>
          </select>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <label>
              <input type="radio" checked={searchMode === 'address'} onChange={() => setSearchMode('address')} /> Address
            </label>
            <label>
              <input type="radio" checked={searchMode === 'hash'} onChange={() => setSearchMode('hash')} /> Tx Hash
            </label>
          </div>
          {searchMode === 'address' ? (
            <input
              type="text"
              value={searchAddress}
              onChange={e => setSearchAddress(e.target.value)}
              placeholder="Enter wallet address to search"
              style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 4 }}
            />
          ) : (
            <input
              type="text"
              value={searchHash}
              onChange={e => setSearchHash(e.target.value)}
              placeholder="Enter transaction hash"
              style={{ flex: 1, minWidth: 220, padding: 8, borderRadius: 4 }}
            />
          )}
          <button
            onClick={async () => {
              setIsSearching(true);
              setSearchError(null);
              setSearchResults([]);
              try {
                if (searchMode === 'address') {
                  if (!searchAddress || !searchBlockchain) return;
                  if (searchBlockchain === 'Stellar') {
                    setSearchError('Stellar address search is not yet supported.');
                    setIsSearching(false);
                    return;
                  }
                  if (searchBlockchain === 'Cardano') {
                    setSearchError('Cardano address search is not yet supported.');
                    setIsSearching(false);
                    return;
                  }
                  const response = await axiosInstance.get(`/crypto/transactions/${searchBlockchain}?walletAddress=${searchAddress}`);
                  if (response.status === 200) {
                    const data = response.data;
                    if (data.success || data.status === 'success') {
                      setSearchResults(data.data || []);
                    } else {
                      setSearchError(data.message || 'No transactions found.');
                    }
                  }
                } else {
                  if (!searchHash || !searchBlockchain) return;
                  const response = await axiosInstance.get(`/crypto/transaction/${searchBlockchain}/${searchHash}`);
                  if (response.status === 200) {
                    const data = response.data;
                    if (data.success || data.status === 'success') {
                      setSearchResults(data.data ? [data.data] : []);
                    } else {
                      setSearchError(data.message || 'No transaction found.');
                    }
                  }
                }
              } catch (error) {
                let errorMsg = 'Error searching.';
                const err = error as any;
                if (err && typeof err === 'object') {
                  if (err.response && typeof err.response === 'object') {
                    const errData = err.response.data;
                    if (errData && errData.errors && Array.isArray(errData.errors) && errData.errors[0]) {
                      errorMsg = errData.errors[0];
                    } else if (errData && errData.message) {
                      errorMsg = errData.message;
                    }
                  } else if (err.message && typeof err.message === 'string') {
                    errorMsg = err.message;
                  }
                }
                setSearchError(errorMsg);
              } finally {
                setIsSearching(false);
              }
            }}
            disabled={isSearching || (searchMode === 'address' ? !searchAddress : !searchHash) || (searchMode === 'address' && (searchBlockchain === 'Stellar' || searchBlockchain === 'Cardano'))}
            style={{ 
              padding: '8px 16px', 
              borderRadius: 4, 
              background: '#007bff', 
              color: '#fff', 
              border: 'none', 
              cursor: 'pointer', 
              opacity: isSearching || (searchMode === 'address' ? !searchAddress : !searchHash) || (searchMode === 'address' && (searchBlockchain === 'Stellar' || searchBlockchain === 'Cardano')) ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: '100px',
              justifyContent: 'center'
            }}
          >
            {isSearching ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  WebkitAnimation: 'spin 1s linear infinite'
                }} />
                <span>Searching...</span>
              </>
            ) : 'Search'}
          </button>
        </div>

        {/* Show search results table if present, else show selected wallet's transactions */}
        {searchError && (
          <div style={{ color: 'red', marginBottom: 12 }}>{searchError}</div>
        )}
        {isSearching ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              WebkitAnimation: 'spin 1s linear infinite'
            }} />
            <div style={{ color: '#666', fontSize: '14px' }}>
              Searching blockchain transactions...
            </div>
          </div>
        ) : searchResults.length > 0 ? (
          <div style={{ marginBottom: 32, overflowX: 'auto' }}>
            <h4>Transactions for {searchBlockchain} address: {searchAddress}</h4>
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, minWidth: 700 }}>
                <thead>
                  <tr style={{ background: '#f0f0f0' }}>
                    <th style={{ padding: 8, border: '1px solid #ddd', maxWidth: 160 }}>Hash</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', maxWidth: 180 }}>From</th>
                    <th style={{ padding: 8, border: '1px solid #ddd', maxWidth: 180 }}>To</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Amount</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Fee</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Status</th>
                    <th style={{ padding: 8, border: '1px solid #ddd' }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {(searchResults || []).map((tx, idx) => (
                    <tr key={tx.transactionHash + idx}>
                      <td style={{ padding: 8, border: '1px solid #ddd', fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.transactionHash}>
                        {tx.transactionHash}
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd', fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.fromAddress}>
                        {tx.fromAddress}
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd', fontFamily: 'monospace', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.toAddress}>
                        {tx.toAddress}
                      </td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{tx.amount} {tx.tokenSymbol}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{tx.fee}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{tx.status}</td>
                      <td style={{ padding: 8, border: '1px solid #ddd' }}>{tx.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          isLoadingTransactions ? (
            <div style={{ textAlign: 'center', padding: 24 }}>Loading transactions...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                borderCollapse: 'collapse',
                fontSize: isMobile ? '14px' : '13px',
                tableLayout: 'fixed',
                width: '100%',
                minWidth: isMobile ? 700 : 'auto',
              }}>
                <thead>
                  <tr>
                    <th style={{ padding: '4px 6px', maxWidth: 120 }}>Hash</th>
                    <th style={{ padding: '4px 6px', maxWidth: 140 }}>From</th>
                    <th style={{ padding: '4px 6px', maxWidth: 140 }}>To</th>
                    <th style={{ padding: '4px 6px', width: 90 }}>Amount</th>
                    <th style={{ padding: '4px 6px', width: 70 }}>Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {(blockchainTransactions || []).map((tx, idx) => (
                    <React.Fragment key={tx.transactionHash + idx}>
                      <tr
                        onClick={() => setExpandedTxIdx(expandedTxIdx === idx ? null : idx)}
                        style={{ cursor: 'pointer', background: expandedTxIdx === idx ? '#f7fafd' : 'transparent' }}
                      >
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.transactionHash}>
                          {tx.transactionHash}
                      </td>
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.fromAddress}>
                          {tx.fromAddress}
                      </td>
                        <td style={{ padding: '4px 6px', fontFamily: 'monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tx.toAddress}>
                          {tx.toAddress}
                      </td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', width: 90 }}>{tx.amount} {tx.tokenSymbol}</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', width: 70 }}>{tx.fee}</td>
                      </tr>
                      {expandedTxIdx === idx && (
                        <tr style={{ background: '#f7fafd' }}>
                          <td colSpan={5} style={{ padding: '0 12px 12px' }}>
                            <div style={{ padding: '16px', background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', marginTop: -1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                <span style={{ color: '#666' }}>Status:</span>
                                <span style={{ fontWeight: 600 }}>{tx.status}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                <span style={{ color: '#666' }}>Timestamp:</span>
                                <span style={{ fontWeight: 600 }}>{tx.timestamp}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 14 }}>
                                <span style={{ color: '#666' }}>Network:</span>
                                <span style={{ fontWeight: 600 }}>{tx.network}</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                <span style={{ color: '#666' }}>Confirmations:</span>
                                <span style={{ fontWeight: 600 }}>{tx.confirmations}</span>
                              </div>
                        </div>
                      </td>
                    </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {(blockchainTransactions || []).length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                        No transactions found for this wallet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );

  // Add Create Wallet Modal
  const CreateWalletModal = () => (
    <div style={{
      ...branding.cryptoPage.modal.overlay,
      position: 'fixed' as 'fixed',
      overflowY: 'auto' as 'auto',
      padding: isMobile ? '16px' : '24px',
      alignItems: isMobile ? 'flex-start' : 'center',
    } as React.CSSProperties}>
      <div style={{
        ...branding.cryptoPage.modal.container,
        maxHeight: isMobile ? '90vh' : 'none',
        overflowY: 'auto' as 'auto',
        margin: isMobile ? 'auto' : '0',
        marginTop: isMobile ? '20px' : 'auto',
        marginBottom: isMobile ? '20px' : 'auto',
      } as React.CSSProperties}>
        <h3 style={branding.cryptoPage.modal.title}>Create New Wallet</h3>
        
        <div style={{ marginBottom: 24 }}>
          <label style={branding.cryptoPage.modal.label}>Select Blockchain</label>
          <select
            value={selectedBlockchain}
            onChange={(e) => setSelectedBlockchain(e.target.value)}
            style={branding.cryptoPage.modal.select}
          >
            <option value="">Select a blockchain</option>
            {SUPPORTED_BLOCKCHAINS.map((chain) => (
              <option key={chain} value={chain}>{chain}</option>
            ))}
          </select>
        </div>

        <div style={branding.cryptoPage.modal.buttonGroup}>
          <button
            onClick={handleCreateWallet}
            disabled={!selectedBlockchain || isCreatingWallet}
            style={{
              ...branding.cryptoPage.modal.primaryButton,
              background: branding.primaryColor,
              color: branding.backgroundColor,
              opacity: (!selectedBlockchain || isCreatingWallet) ? 0.7 : 1,
            }}
          >
            {isCreatingWallet ? 'Creating...' : 'Create Wallet'}
          </button>
          <button
            onClick={() => setShowCreateWalletModal(false)}
            style={branding.cryptoPage.modal.secondaryButton}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Add Import Wallet Modal
  const ImportWalletModal = () => (
    <div style={{
      ...branding.cryptoPage.modal.overlay,
      position: 'fixed' as 'fixed',
      overflowY: 'auto' as 'auto',
      padding: isMobile ? '16px' : '24px',
      alignItems: isMobile ? 'flex-start' : 'center',
    } as React.CSSProperties}>
      <div style={{
        ...branding.cryptoPage.modal.container,
        maxWidth: '600px',
        maxHeight: isMobile ? '90vh' : '90vh',
        overflowY: 'auto' as 'auto',
        margin: isMobile ? 'auto' : '0',
        marginTop: isMobile ? '20px' : 'auto',
        marginBottom: isMobile ? '20px' : 'auto',
      } as React.CSSProperties}>
        <h3 style={branding.cryptoPage.modal.title}>Import Existing Wallet</h3>
        
        {/* Security Warning */}
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '16px' }}>⚠️</span>
            <strong style={{ color: '#856404' }}>Security Notice</strong>
          </div>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#856404',
            lineHeight: '1.4',
          }}>
            Your private key or recovery phrase will be encrypted and stored securely. 
            Never share these credentials with anyone. This information is used only to import your wallet.
          </p>
        </div>

        {/* Import Method Selection */}
        <div style={{ marginBottom: 24 }}>
          <label style={branding.cryptoPage.modal.label}>Import Method</label>
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '8px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '6px',
              border: `2px solid ${importMethod === 'privateKey' ? '#007bff' : '#ddd'}`,
              background: importMethod === 'privateKey' ? '#f0f8ff' : 'transparent',
            }}>
              <input
                type="radio"
                checked={importMethod === 'privateKey'}
                onChange={() => setImportMethod('privateKey')}
                style={{ margin: 0 }}
              />
              Private Key
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '6px',
              border: `2px solid ${importMethod === 'recoveryPhrase' ? '#007bff' : '#ddd'}`,
              background: importMethod === 'recoveryPhrase' ? '#f0f8ff' : 'transparent',
            }}>
              <input
                type="radio"
                checked={importMethod === 'recoveryPhrase'}
                onChange={() => setImportMethod('recoveryPhrase')}
                style={{ margin: 0 }}
              />
              Recovery Phrase
            </label>
          </div>
        </div>

        {/* Blockchain Selection */}
        <div style={{ marginBottom: 24 }}>
          <label style={branding.cryptoPage.modal.label}>Select Blockchain</label>
          <select
            value={selectedImportBlockchain}
            onChange={(e) => setSelectedImportBlockchain(e.target.value)}
            style={branding.cryptoPage.modal.select}
          >
            <option value="">Select a blockchain</option>
            {SUPPORTED_BLOCKCHAINS.map((chain) => (
              <option key={chain} value={chain}>{chain}</option>
            ))}
          </select>
        </div>

        {/* Private Key Input */}
        {importMethod === 'privateKey' && (
          <div style={{ marginBottom: 24 }}>
            <label style={branding.cryptoPage.modal.label}>Private Key</label>
            <textarea
              value={importPrivateKey}
              onChange={(e) => setImportPrivateKey(e.target.value)}
              placeholder="Enter your private key (with or without 0x prefix)"
              style={{
                ...branding.cryptoPage.modal.input,
                minHeight: '80px',
                fontFamily: 'monospace',
                fontSize: '12px',
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px',
            }}>
              Enter your private key. It will be encrypted and stored securely.
            </div>
          </div>
        )}

        {/* Recovery Phrase Input */}
        {importMethod === 'recoveryPhrase' && (
          <>
            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Recovery Phrase</label>
              <textarea
                value={importRecoveryPhrase}
                onChange={(e) => setImportRecoveryPhrase(e.target.value)}
                placeholder="Enter your 12, 15, 18, 21, or 24 word recovery phrase"
                style={{
                  ...branding.cryptoPage.modal.input,
                  minHeight: '80px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              />
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px',
              }}>
                Enter your recovery phrase (mnemonic). Words should be separated by spaces.
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Password (Optional)</label>
              <input
                type="password"
                value={importPassword}
                onChange={(e) => setImportPassword(e.target.value)}
                placeholder="Enter password if your wallet is encrypted"
                style={branding.cryptoPage.modal.input}
              />
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px',
              }}>
                Only required if your wallet is password-protected.
              </div>
            </div>
          </>
        )}

        {/* Wallet Name (Optional) */}
        <div style={{ marginBottom: 24 }}>
          <label style={branding.cryptoPage.modal.label}>Wallet Name (Optional)</label>
          <input
            type="text"
            value={importWalletName}
            onChange={(e) => setImportWalletName(e.target.value)}
            placeholder="Give your wallet a custom name"
            style={branding.cryptoPage.modal.input}
          />
        </div>

        {/* Error Display */}
        {importError && (
          <div style={{
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            padding: '16px',
            color: '#721c24',
            marginBottom: '20px',
          }}>
            <div style={{ marginBottom: '8px' }}>❌ Error</div>
            <div style={{ fontSize: '14px' }}>{importError}</div>
          </div>
        )}

        <div style={branding.cryptoPage.modal.buttonGroup}>
          <button
            onClick={handleImportWallet}
            disabled={!selectedImportBlockchain || isImportingWallet || 
                     (importMethod === 'privateKey' && !importPrivateKey.trim()) ||
                     (importMethod === 'recoveryPhrase' && !importRecoveryPhrase.trim())}
            style={{
              ...branding.cryptoPage.modal.primaryButton,
              background: '#28a745',
              color: '#fff',
              opacity: (!selectedImportBlockchain || isImportingWallet || 
                       (importMethod === 'privateKey' && !importPrivateKey.trim()) ||
                       (importMethod === 'recoveryPhrase' && !importRecoveryPhrase.trim())) ? 0.7 : 1,
            }}
          >
            {isImportingWallet ? 'Importing...' : 'Import Wallet'}
          </button>
          <button
            onClick={() => {
              setShowImportWalletModal(false);
              setImportError(null);
              setImportMethod('privateKey');
              setImportPrivateKey('');
              setImportRecoveryPhrase('');
              setImportPassword('');
              setImportWalletName('');
              setSelectedImportBlockchain('');
            }}
            style={branding.cryptoPage.modal.secondaryButton}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // Add Investment Modal
  const InvestmentModal = useCallback(() => (
    <div style={{
      ...branding.cryptoPage.modal.overlay,
      position: 'fixed' as 'fixed'
    }}>
      <div style={{
        ...branding.cryptoPage.modal.container,
        maxWidth: '500px',
      }}>
        <h3 style={branding.cryptoPage.modal.title}>Invest in Asset-Backed Token</h3>
        
        {selectedAssetToken && (
          <>
            {/* Token Information */}
            <div style={{
              background: '#f8f9fa',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                  {selectedAssetToken.tokenSymbol}
                </h4>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: selectedAssetToken.status === 'ACTIVE' ? '#28a745' : '#dc3545',
                  color: '#fff',
                }}>
                  {selectedAssetToken.status}
                </span>
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>Asset:</strong> {selectedAssetToken.assetName}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Asset Value:</strong> ${selectedAssetToken.assetValue?.toLocaleString()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Token Price:</strong> ${selectedAssetToken.currentPrice?.toFixed(6)}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>Min Investment:</strong> ${selectedAssetToken.minimumInvestment}
              </div>
              <div>
                <strong>Management Fee:</strong> {(selectedAssetToken.managementFee * 100).toFixed(2)}%
              </div>
            </div>

            {/* Investment Amount */}
            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Investment Amount (USD)</label>
              <input
                ref={investmentInputRef}
                key={`investment-amount-${selectedAssetToken.id}`}
                type="number"
                value={investmentAmount}
                onChange={(e) => handleInvestmentAmountChange(e.target.value)}
                placeholder={`Minimum $${selectedAssetToken.minimumInvestment}`}
                style={branding.cryptoPage.modal.input}
                autoFocus
              />
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px',
              }}>
                Enter the amount you want to invest in this asset-backed token.
              </div>
            </div>

            {/* Investment Summary */}
            {investmentAmount && parseFloat(investmentAmount) >= selectedAssetToken.minimumInvestment && (
              <div style={{
                background: '#e8f5e8',
                border: '1px solid #28a745',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
              }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#28a745' }}>Investment Summary</h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Investment Amount:</span>
                  <span style={{ fontWeight: '600' }}>${parseFloat(investmentAmount).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Tokens to Receive:</span>
                  <span style={{ fontWeight: '600' }}>
                    {(parseFloat(investmentAmount) / selectedAssetToken.currentPrice).toFixed(8)} {selectedAssetToken.tokenSymbol}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Management Fee:</span>
                  <span style={{ fontWeight: '600' }}>
                    ${(parseFloat(investmentAmount) * selectedAssetToken.managementFee).toFixed(2)}/year
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Backed by:</span>
                  <span style={{ fontWeight: '600' }}>{selectedAssetToken.assetName}</span>
                </div>
              </div>
            )}

            {/* Error Display */}
            {investmentError && (
              <div style={{
                background: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                padding: '16px',
                color: '#721c24',
                marginBottom: '20px',
              }}>
                <div style={{ marginBottom: '8px' }}>❌ Error</div>
                <div style={{ fontSize: '14px' }}>{investmentError}</div>
              </div>
            )}

            <div style={branding.cryptoPage.modal.buttonGroup}>
              <button
                onClick={handleInvestInAssetToken}
                disabled={!investmentAmount || parseFloat(investmentAmount) < selectedAssetToken.minimumInvestment || isInvesting}
                style={{
                  ...branding.cryptoPage.modal.primaryButton,
                  background: '#28a745',
                  color: '#fff',
                  opacity: (!investmentAmount || parseFloat(investmentAmount) < selectedAssetToken.minimumInvestment || isInvesting) ? 0.7 : 1,
                }}
              >
                {isInvesting ? 'Processing...' : 'Confirm Investment'}
              </button>
              <button
                onClick={() => {
                  setShowAssetTokenModal(false);
                  setSelectedAssetToken(null);
                  setInvestmentAmount('');
                  setInvestmentError(null);
                }}
                style={branding.cryptoPage.modal.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ), [selectedAssetToken, investmentAmount, isInvesting, investmentError]);

  // Add Asset-Backed Tokens Section
  const AssetBackedTokensSection = () => (
    <div style={{
      padding: isMobile ? '20px' : '24px',
      paddingBottom: isMobile ? '60px' : '80px',
      marginBottom: '40px',
      background: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb'
    } as React.CSSProperties}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 12,
      }}>
        <h3 style={{
          margin: branding.cryptoPage.wallets.header.title.margin,
          color: branding.cryptoPage.wallets.header.title.color,
          fontSize: isMobile ? branding.cryptoPage.wallets.header.title.fontSize.mobile : branding.cryptoPage.wallets.header.title.fontSize.desktop,
          fontWeight: branding.cryptoPage.wallets.header.title.fontWeight,
        }}>Asset-Backed Tokens</h3>
        <div style={{
          display: 'flex',
          gap: 12,
          width: isMobile ? '100%' : 'auto'
        }}>
          <button
            onClick={() => fetchAssetTokens()}
            style={{
              padding: '10px 20px',
              background: '#17a2b8',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: isMobile ? 1 : 'auto',
              height: '40px',
              justifyContent: 'center',
            }}
          >
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: 16,
      }}>
        {assetTokens.map((token, index) => (
          <div
            key={index}
            style={{
              padding: isMobile ? '16px' : '20px',
              paddingBottom: isMobile ? '24px' : '32px',
              background: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
              marginBottom: '16px'
            } as React.CSSProperties}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}>
              <span style={{
                background: token.assetType === 'GOLD' ? '#ffd700' : 
                           token.assetType === 'SILVER' ? '#c0c0c0' :
                           token.assetType === 'DIAMOND' ? '#b9f2ff' :
                           token.assetType === 'CAR' ? '#ff6b6b' :
                           token.assetType === 'PROPERTY' ? '#4ecdc4' : '#6c757d',
                color: token.assetType === 'GOLD' ? '#000' : '#fff',
                padding: '6px 12px',
                marginLeft: '8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'inline-block'
              }}>
                {token.assetType}
              </span>
              <span style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                background: token.status === 'ACTIVE' ? '#28a745' : '#dc3545',
                color: '#fff',
              }}>
                {token.status}
              </span>
            </div>
            
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                {token.tokenSymbol}
              </h4>
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                {token.assetName}
              </p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Asset Value:</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  ${token.assetValue?.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Token Price:</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  ${token.currentPrice?.toFixed(6)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Market Cap:</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  ${token.marketCap?.toLocaleString()}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Min Investment:</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  ${token.minimumInvestment}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>Management Fee:</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>
                  {(token.managementFee * 100).toFixed(2)}%
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                <strong>Location:</strong> {token.assetLocation}
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                <strong>Certificate:</strong> {token.certificateNumber}
              </p>
              {token.insuranceCoverage && (
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                  <strong>Insurance:</strong> ${token.insuranceCoverage?.toLocaleString()}
                </p>
              )}
              
              {/* Blockchain Information */}
              {token.blockchain && (
                <div style={{ 
                  marginTop: '12px', 
                  padding: '8px', 
                  background: '#f8f9fa', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span><strong>Blockchain:</strong></span>
                    <span>{token.blockchain}</span>
                  </div>
                  {token.deploymentStatus && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span><strong>Status:</strong></span>
                      <span style={{ 
                        color: token.deploymentStatus === 'DEPLOYED' ? '#28a745' : 
                               token.deploymentStatus === 'DEPLOYING' ? '#ffc107' : 
                               token.deploymentStatus === 'FAILED' ? '#dc3545' : '#6c757d'
                      }}>
                        {token.deploymentStatus}
                      </span>
                    </div>
                  )}
                  {token.contractAddress && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span><strong>Contract:</strong></span>
                      <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>
                        {token.contractAddress.substring(0, 8)}...
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'center',
              padding: '0 12px'
            }}>
              <button
                onClick={() => {
                  setSelectedAssetToken(token);
                  setShowAssetTokenModal(true);
                }}
                style={{
                  padding: '8px 24px',
                  background: '#007bff',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  flex: '0 0 80%',
                  maxWidth: '80%',
                  width: '80%'
                }}
              >
                💰 Invest
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Add User Investments Section
  const UserInvestmentsSection = () => (
    <div style={{
      ...branding.cryptoPage.wallets.card,
      padding: isMobile ? branding.cryptoPage.wallets.card.padding.mobile : branding.cryptoPage.wallets.card.padding.desktop,
      marginTop: '24px'
    } as React.CSSProperties}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 16 : 12,
      }}>
        <h3 style={{
          margin: branding.cryptoPage.wallets.header.title.margin,
          color: branding.cryptoPage.wallets.header.title.color,
          fontSize: isMobile ? branding.cryptoPage.wallets.header.title.fontSize.mobile : branding.cryptoPage.wallets.header.title.fontSize.desktop,
          fontWeight: branding.cryptoPage.wallets.header.title.fontWeight,
        }}>My Asset Investments</h3>
        <div style={{
          display: 'flex',
          gap: 12,
          width: isMobile ? '100%' : 'auto'
        }}>
          <button
            onClick={() => fetchUserInvestments()}
            style={{
              padding: '10px 20px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flex: isMobile ? 1 : 'auto',
              height: '40px',
              justifyContent: 'center',
            }}
          >
            <span>🔄</span> Refresh
          </button>
        </div>
      </div>

      {loadingInvestments ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="loading-spinner"></div>
          <p style={{ marginTop: '16px', color: '#666' }}>Loading your investments...</p>
        </div>
      ) : userInvestments.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#666',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px dashed #dee2e6'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>No Investments Yet</h4>
          <p style={{ margin: 0, fontSize: '14px' }}>
            You haven't invested in any asset-backed tokens yet.
            Browse the available tokens above to start investing!
          </p>
        </div>
      ) : (
        <div style={{
          overflowX: 'auto',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
          }}>
            <thead>
              <tr style={{
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb',
              }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Asset Token</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Status</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Investment Amount</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Tokens Owned</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Purchase Price</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Current Value</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Profit/Loss</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Investment Date</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userInvestments.map((investment, index) => (
                <tr key={index} style={{
                  borderBottom: '1px solid #f3f4f6',
                  background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                }}>
                  <td style={{
                    padding: '12px 16px',
                    verticalAlign: 'top',
                  }}>
                    <div>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '16px',
                        color: '#1f2937',
                        marginBottom: '4px',
                      }}>
                        {investment.assetTokenSymbol}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                      }}>
                        {investment.assetTokenName}
                      </div>
                    </div>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    verticalAlign: 'top',
                  }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: investment.status === 'ACTIVE' ? '#d4edda' : '#f8d7da',
                      color: investment.status === 'ACTIVE' ? '#155724' : '#721c24',
                    }}>
                      {investment.status}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    verticalAlign: 'top',
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                  }}>
                    ${investment.investmentAmount?.toLocaleString()}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    verticalAlign: 'top',
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                  }}>
                    {investment.tokensPurchased?.toFixed(6)}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    verticalAlign: 'top',
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                  }}>
                    ${investment.purchasePricePerToken?.toFixed(6)}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    verticalAlign: 'top',
                    fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                  }}>
                    ${investment.currentValue?.toLocaleString()}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    verticalAlign: 'top',
                  }}>
                    <div style={{
                      color: investment.profitLoss >= 0 ? '#10b981' : '#ef4444',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}>
                      {investment.profitLoss >= 0 ? '+' : ''}${investment.profitLoss?.toFixed(2)}
                    </div>
                    <div style={{
                      color: investment.profitLoss >= 0 ? '#10b981' : '#ef4444',
                      fontSize: '12px',
                    }}>
                      ({investment.profitLossPercentage?.toFixed(2)}%)
                    </div>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    verticalAlign: 'top',
                    fontSize: '12px',
                    color: '#6b7280',
                  }}>
                    {new Date(investment.investmentDate).toLocaleDateString()}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    verticalAlign: 'top',
                  }}>
                    <button
                      onClick={() => {
                        setSelectedInvestment(investment);
                        setShowInvestmentDetailsModal(true);
                      }}
                      style={{
                        padding: '6px 12px',
                        background: '#6c757d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      📈 Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div style={{ textAlign: 'center', marginTop: 64 }}>Loading crypto portfolio...</div>;
  }
  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: 64 }}>{error}</div>;
  }

  return (
    <>
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      background: branding.pageBackground,
      padding: isMobile ? '0' : '0',
      paddingBottom: isMobile ? branding.cryptoPage.layout.paddingBottom.mobile : branding.cryptoPage.layout.paddingBottom.desktop,
      margin: 0,
    }}>
      <div style={{
        maxWidth: branding.cryptoPage.layout.maxWidth,
        margin: isMobile ? '0 auto' : `${branding.cryptoPage.layout.marginTop.desktop} auto 0 auto`,
        padding: branding.cryptoPage.layout.padding,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: 32,
          gap: isMobile ? 24 : 0,
        }}>
          <div>
            <h1 style={{
              color: branding.cryptoPage.header.title.color,
              fontWeight: branding.cryptoPage.header.title.fontWeight,
              fontSize: isMobile ? branding.cryptoPage.header.title.fontSize.mobile : branding.cryptoPage.header.title.fontSize.desktop,
              margin: 0,
              marginBottom: branding.cryptoPage.header.title.marginBottom,
            }}>{branding.cryptoPage.title}</h1>
            <div style={{ 
              fontSize: isMobile ? branding.cryptoPage.header.subtitle.fontSize.mobile : branding.cryptoPage.header.subtitle.fontSize.desktop, 
              color: branding.cryptoPage.header.subtitle.color 
            }}>
              {branding.cryptoPage.subtitle}
            </div>
          </div>
          <div style={{
            display: 'flex',
            gap: isMobile ? 8 : 12,
            alignItems: 'center',
            flexWrap: (isMobile ? 'wrap' : 'nowrap') as 'wrap' | 'nowrap',
          } as React.CSSProperties}>
            {['24h', '7d', '30d', '90d', '1y'].map(timeframe => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeframe(timeframe)}
                style={{
                  padding: branding.cryptoPage.timeframe.button.padding,
                  borderRadius: branding.cryptoPage.timeframe.button.borderRadius,
                  border: '1.5px solid',
                  borderColor: selectedTimeframe === timeframe ? branding.cryptoPage.timeframe.button.active.borderColor : branding.cryptoPage.timeframe.button.inactive.borderColor,
                  background: selectedTimeframe === timeframe ? branding.cryptoPage.timeframe.button.active.background : branding.cryptoPage.timeframe.button.inactive.background,
                  color: selectedTimeframe === timeframe ? branding.cryptoPage.timeframe.button.active.color : branding.cryptoPage.timeframe.button.inactive.color,
                  cursor: 'pointer',
                  fontSize: branding.cryptoPage.timeframe.button.fontSize,
                  fontWeight: branding.cryptoPage.timeframe.button.fontWeight,
                  transition: branding.cryptoPage.timeframe.button.transition,
                  whiteSpace: branding.cryptoPage.timeframe.button.whiteSpace,
                }}
              >
                {timeframe}
              </button>
            ))}
            <button
              onClick={() => navigate('/stablecoin')}
              style={{
                padding: branding.cryptoPage.timeframe.button.padding,
                borderRadius: branding.cryptoPage.timeframe.button.borderRadius,
                border: 'none',
                background: branding.primaryColor,
                color: branding.backgroundColor,
                cursor: 'pointer',
                fontSize: branding.cryptoPage.timeframe.button.fontSize,
                fontWeight: branding.cryptoPage.timeframe.button.fontWeight,
                transition: branding.cryptoPage.timeframe.button.transition,
                whiteSpace: branding.cryptoPage.timeframe.button.whiteSpace,
                marginLeft: isMobile ? 0 : 16,
                marginTop: isMobile ? 12 : 0,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Stablecoin Payments
            </button>
          </div>
        </div>

        {/* Portfolio Summary */}
        <div style={{
          background: branding.cryptoPage.portfolioSummary.background,
          borderRadius: branding.cryptoPage.portfolioSummary.borderRadius,
          padding: isMobile ? branding.cryptoPage.portfolioSummary.padding.mobile : branding.cryptoPage.portfolioSummary.padding.desktop,
          color: branding.cryptoPage.portfolioSummary.color,
          marginBottom: 24,
          boxShadow: branding.cryptoPage.portfolioSummary.boxShadow,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 16 : 0,
          }}>
            <div>
              <div style={{ 
                fontSize: isMobile ? branding.cryptoPage.portfolioSummary.totalValue.label.fontSize.mobile : branding.cryptoPage.portfolioSummary.totalValue.label.fontSize.desktop, 
                opacity: branding.cryptoPage.portfolioSummary.totalValue.label.opacity, 
                  marginBottom: branding.cryptoPage.portfolioSummary.totalValue.label.marginBottom,
                }}>
                  Total Portfolio Value
                </div>
              <div style={{ 
                fontSize: isMobile ? branding.cryptoPage.portfolioSummary.totalValue.amount.fontSize.mobile : branding.cryptoPage.portfolioSummary.totalValue.amount.fontSize.desktop, 
                  fontWeight: branding.cryptoPage.portfolioSummary.totalValue.amount.fontWeight,
                }}>
                  {cryptoPricesLoading ? (
                    <span style={{ color: '#007bff' }}>Loading...</span>
                  ) : (
                    <>${calculatePortfolioTotals().totalValue.toFixed(2)}</>
                  )}
            </div>
              </div>
              <div style={{
                display: 'flex',
                gap: isMobile ? 16 : 24,
                flexWrap: 'wrap',
              }}>
                <div style={{ textAlign: 'center' }}>
              <div style={{ 
                fontSize: isMobile ? branding.cryptoPage.portfolioSummary.change24h.label.fontSize.mobile : branding.cryptoPage.portfolioSummary.change24h.label.fontSize.desktop, 
                opacity: branding.cryptoPage.portfolioSummary.change24h.label.opacity, 
                    marginBottom: branding.cryptoPage.portfolioSummary.change24h.label.marginBottom,
                  }}>
                    24h Change
                  </div>
              <div style={{ 
                fontSize: isMobile ? branding.cryptoPage.portfolioSummary.change24h.percentage.fontSize.mobile : branding.cryptoPage.portfolioSummary.change24h.percentage.fontSize.desktop, 
                fontWeight: branding.cryptoPage.portfolioSummary.change24h.percentage.fontWeight, 
                    color: calculatePortfolioTotals().change24h >= 0 ? branding.successColor : branding.errorColor,
                  }}>
                    {calculatePortfolioTotals().change24h >= 0 ? '+' : ''}{calculatePortfolioTotals().change24h.toFixed(2)}%
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: isMobile ? branding.cryptoPage.portfolioSummary.change24h.label.fontSize.mobile : branding.cryptoPage.portfolioSummary.change24h.label.fontSize.desktop,
                    opacity: branding.cryptoPage.portfolioSummary.change24h.label.opacity,
                    marginBottom: branding.cryptoPage.portfolioSummary.change24h.label.marginBottom,
                  }}>
                    Total Coins
                  </div>
                  <div style={{
                    fontSize: isMobile ? branding.cryptoPage.portfolioSummary.change24h.percentage.fontSize.mobile : branding.cryptoPage.portfolioSummary.change24h.percentage.fontSize.desktop,
                    fontWeight: branding.cryptoPage.portfolioSummary.change24h.percentage.fontWeight,
                    color: branding.textColor,
                  }}>
                    {cryptoPortfolio?.coins?.length || 0}
                  </div>
              </div>
            </div>
          </div>
        </div>

        <RetailPlatformAirdrops />

        {/* Wallet Section */}
        <WalletSection />

        {/* Coin Cards */}
        <div style={branding.cryptoPage.coinCards.grid}>
          {(cryptoPortfolio?.coins || []).map((coin: Coin) => (
            <div
              key={coin.id}
              style={branding.cryptoPage.coinCards.card}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: branding.cryptoPage.coinCards.coinIcon.width,
                    height: branding.cryptoPage.coinCards.coinIcon.height,
                    borderRadius: branding.cryptoPage.coinCards.coinIcon.borderRadius,
                    background: coin.color,
                    color: branding.cryptoPage.coinCards.coinIcon.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: branding.cryptoPage.coinCards.coinIcon.fontSize,
                    fontWeight: branding.cryptoPage.coinCards.coinIcon.fontWeight,
                  }}>
                    {coin.icon}
                  </div>
                  <div>
                    <div style={{ 
                      fontWeight: branding.cryptoPage.coinCards.coinName.fontWeight, 
                      fontSize: branding.cryptoPage.coinCards.coinName.fontSize,
                      color: branding.cryptoPage.coinCards.coinName.color
                    }}>{coin.name}</div>
                    <div style={{ 
                      color: branding.cryptoPage.coinCards.coinSymbol.color, 
                      fontSize: branding.cryptoPage.coinCards.coinSymbol.fontSize 
                    }}>{coin.symbol}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontWeight: branding.cryptoPage.coinCards.coinValue.fontWeight, 
                    fontSize: branding.cryptoPage.coinCards.coinValue.fontSize,
                    color: branding.cryptoPage.coinCards.coinValue.color,
                    marginBottom: 4
                  }}>
                    Balance: $
                    {Number.isFinite(Number(coin.price))
                      ? (parseFloat(coin.amount || '0') * Number(coin.price)).toFixed(2)
                      : '—'}
                  </div>
                  <div style={{ 
                    color: coin.change24h.startsWith('+') ? branding.successColor : branding.errorColor, 
                    fontSize: branding.cryptoPage.coinCards.coinChange.fontSize, 
                    fontWeight: branding.cryptoPage.coinCards.coinChange.fontWeight 
                  }}>
                    {coin.change24h}
                  </div>
                </div>
              </div>
              <div style={{ 
                borderTop: branding.cryptoPage.coinCards.details.borderTop, 
                paddingTop: branding.cryptoPage.coinCards.details.paddingTop 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ 
                    color: branding.cryptoPage.coinCards.details.label.color, 
                    fontSize: branding.cryptoPage.coinCards.details.label.fontSize 
                  }}>Amount</div>
                  <div style={{ 
                    fontWeight: branding.cryptoPage.coinCards.details.value.fontWeight,
                    color: branding.cryptoPage.coinCards.details.value.color
                  }}>{coin.amount} {coin.symbol}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ 
                    color: branding.cryptoPage.coinCards.details.label.color, 
                    fontSize: branding.cryptoPage.coinCards.details.label.fontSize 
                  }}>Price</div>
                  <div style={{ 
                    fontWeight: branding.cryptoPage.coinCards.details.value.fontWeight,
                    color: branding.cryptoPage.coinCards.details.value.color
                  }}>{branding.currency.symbol}{formatUsdAmount(coin.price)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Transaction History */}
        <div style={{
          ...branding.cryptoPage.transactionTable.container,
          padding: isMobile ? branding.cryptoPage.transactionTable.container.padding.mobile : branding.cryptoPage.transactionTable.container.padding.desktop
        }}>
          <h3 style={{
              color: branding.cryptoPage.transactionTable.title.color,
            fontSize: isMobile ? branding.cryptoPage.transactionTable.title.fontSize.mobile : branding.cryptoPage.transactionTable.title.fontSize.desktop,
            fontWeight: branding.cryptoPage.transactionTable.title.fontWeight,
              margin: 0,
              marginBottom: branding.cryptoPage.transactionTable.title.marginBottom,
            }}>
              Recent Transactions
            </h3>
            <div style={{
              overflowX: 'auto',
            }}>
            <table style={{
              width: '100%',
                borderCollapse: 'collapse',
                fontSize: isMobile ? branding.cryptoPage.transactionTable.table.fontSize.mobile : branding.cryptoPage.transactionTable.table.fontSize.desktop,
            }}>
              <thead>
                  <tr style={{
                    color: branding.cryptoPage.transactionTable.header.color,
                  }}>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Type</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Coin</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Amount</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Price</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Total</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Date</th>
                    <th style={{
                      padding: branding.cryptoPage.transactionTable.header.padding,
                      textAlign: 'left',
                      fontWeight: branding.cryptoPage.transactionTable.header.fontWeight,
                    }}>Status</th>
                </tr>
              </thead>
              <tbody>
                  {cryptoTransactions.length > 0 ? (
                    cryptoTransactions.map((transaction: CryptoTransaction) => (
                      <tr key={transaction.id} style={{
                        borderBottom: branding.cryptoPage.transactionTable.cell.borderBottom,
                      }}>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '16px' }}>{transaction.icon}</span>
                            <span style={{
                              color: transaction.type === 'buy' ? branding.successColor : branding.errorColor,
                              fontWeight: '500',
                            }}>
                              {transaction.type.toUpperCase()}
                            </span>
                          </div>
                    </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: '16px' }}>{transaction.icon}</span>
                            <span>{transaction.coin}</span>
                          </div>
                    </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          {transaction.amount} {transaction.symbol}
                    </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          {formatUsdCell(transaction.price)}
                        </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                          fontWeight: '600',
                        }}>
                          {formatUsdCell(transaction.total)}
                        </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          {transaction.date}
                        </td>
                        <td style={{
                          padding: branding.cryptoPage.transactionTable.cell.padding,
                        }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: transaction.status === 'completed' ? branding.successColor : branding.warningColor,
                            color: '#fff',
                          }}>
                            {transaction.status}
                          </span>
                    </td>
                  </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                        No transactions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>

          {/* Asset-Backed Tokens Section */}
          <AssetBackedTokensSection />

          {/* User Investments Section */}
          <UserInvestmentsSection />
        </div>
      </div>

      {/* Buy Modal */}
      {showBuyModal && (
        <div style={{
          ...branding.cryptoPage.modal.overlay,
          position: 'fixed' as 'fixed',
          overflowY: 'auto' as 'auto',
          padding: isMobile ? '16px' : '24px',
          alignItems: isMobile ? 'flex-start' : 'center',
        } as React.CSSProperties}>
          <div style={{
            ...branding.cryptoPage.modal.container,
            maxHeight: isMobile ? '90vh' : 'none',
            overflowY: 'auto' as 'auto',
            margin: isMobile ? 'auto' : '0',
            marginTop: isMobile ? '20px' : 'auto',
            marginBottom: isMobile ? '20px' : 'auto',
          } as React.CSSProperties}>
            <h3 style={branding.cryptoPage.modal.title}>Buy Cryptocurrency</h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Select Coin</label>
              <select
                value={selectedCoin?.symbol || ''}
                onChange={handleCoinSelect}
                style={branding.cryptoPage.modal.select}
              >
                <option value="">Select a coin</option>
                {(cryptoPortfolio?.coins || []).map((coin: Coin) => (
                  <option key={coin.symbol} value={coin.symbol}>
                    {coin.name} ({coin.symbol})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                style={branding.cryptoPage.modal.input}
              />
            </div>

            {selectedCoin && amount && (
              <div style={branding.cryptoPage.modal.summary}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Price per {selectedCoin?.symbol}</span>
                  <span>${selectedCoin?.price?.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                  <span>Total Cost</span>
                  <span>${((selectedCoin?.price || 0) * parseFloat(amount || '0')).toFixed(2)}</span>
                </div>
              </div>
            )}

            <div style={branding.cryptoPage.modal.buttonGroup}>
              <button
                onClick={handleBuyCrypto}
                disabled={!selectedCoin || !amount || isProcessing}
                style={{
                  ...branding.cryptoPage.modal.primaryButton,
                  background: branding.successColor,
                  color: branding.backgroundColor,
                  opacity: (!selectedCoin || !amount || isProcessing) ? 0.7 : 1,
                }}
              >
                {isProcessing ? 'Processing...' : 'Buy Now'}
              </button>
              <button
                onClick={() => setShowBuyModal(false)}
                style={branding.cryptoPage.modal.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Modal */}
      {showSendModal && (
        <div style={{
          ...branding.cryptoPage.modal.overlay,
          position: 'fixed' as 'fixed',
          overflowY: 'auto' as 'auto',
          padding: isMobile ? '16px' : '24px',
          alignItems: isMobile ? 'flex-start' : 'center',
        } as React.CSSProperties}>
          <div style={{
            ...branding.cryptoPage.modal.container,
            maxHeight: isMobile ? '90vh' : 'none',
            overflowY: 'auto' as 'auto',
            margin: isMobile ? 'auto' : '0',
            marginTop: isMobile ? '20px' : 'auto',
            marginBottom: isMobile ? '20px' : 'auto',
          } as React.CSSProperties}>
            <h3 style={branding.cryptoPage.modal.title}>Send Cryptocurrency</h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Select Coin</label>
              <select
                value={selectedCoin?.symbol || ''}
                onChange={handleCoinSelect}
                style={branding.cryptoPage.modal.select}
              >
                <option value="">Select a coin</option>
                {(cryptoPortfolio?.coins || []).map((coin: Coin) => (
                  <option key={coin.symbol} value={coin.symbol}>
                    {coin.name} ({coin.symbol})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Amount</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                style={branding.cryptoPage.modal.input}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={branding.cryptoPage.modal.label}>Recipient Address</label>
              <input
                type="text"
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                placeholder="Enter recipient address"
                style={branding.cryptoPage.modal.input}
              />
            </div>

            {selectedCoin && amount && (
              <div style={branding.cryptoPage.modal.summary}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span>Available Balance</span>
                  <span>{selectedCoin?.amount} {selectedCoin?.symbol}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                  <span>Total Value</span>
                  <span>${((selectedCoin?.price || 0) * parseFloat(amount || '0')).toFixed(2)}</span>
                </div>
              </div>
            )}

            <div style={branding.cryptoPage.modal.buttonGroup}>
              <button
                onClick={handleSendCrypto}
                disabled={!selectedCoin || !amount || !recipientAddress || isProcessing}
                style={{
                  ...branding.cryptoPage.modal.primaryButton,
                  background: branding.primaryColor,
                  color: branding.backgroundColor,
                  opacity: (!selectedCoin || !amount || !recipientAddress || isProcessing) ? 0.7 : 1,
                }}
              >
                {isProcessing ? 'Processing...' : 'Send Now'}
              </button>
              <button
                onClick={() => setShowSendModal(false)}
                style={branding.cryptoPage.modal.secondaryButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateWalletModal && <CreateWalletModal />}
      {showImportWalletModal && <ImportWalletModal />}
      {showAssetTokenModal && <InvestmentModal />}
      
      {/* Investment Details Modal */}
      {showInvestmentDetailsModal && selectedInvestment && (
        <div className="modal-overlay">
          <div className="user-details-modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Investment Details - {selectedInvestment.assetTokenSymbol}</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowInvestmentDetailsModal(false);
                  setSelectedInvestment(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Investment Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Asset Token</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedInvestment.assetTokenSymbol} - {selectedInvestment.assetTokenName}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Investment Status</div>
        <div style={{
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: selectedInvestment.status === 'ACTIVE' ? '#10b981' : '#ef4444'
                    }}>
                      {selectedInvestment.status}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Investment Amount</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.investmentAmount?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Tokens Owned</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {selectedInvestment.tokensPurchased?.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Purchase Price</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.purchasePricePerToken?.toFixed(6)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Current Value</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      ${selectedInvestment.currentValue?.toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Profit/Loss</div>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '600',
                      color: selectedInvestment.profitLoss >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {selectedInvestment.profitLoss >= 0 ? '+' : ''}${selectedInvestment.profitLoss?.toFixed(2)}
                      <span style={{ fontSize: '14px', marginLeft: '8px' }}>
                        ({selectedInvestment.profitLossPercentage?.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Investment Date</div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                      {new Date(selectedInvestment.investmentDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Blockchain Transaction Details */}
              <div style={{
                borderTop: '1px solid #e5e7eb', 
                paddingTop: '24px',
                marginTop: '24px'
              }}>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Blockchain Transaction Details</h4>
                
                {selectedInvestment.transactionHash ? (
                  <div style={{ 
                    background: '#f8f9fa', 
                    padding: '16px', 
                borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Transaction Hash</div>
                        <div style={{ 
                          fontSize: '12px', 
                          fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                          color: '#1f2937',
                wordBreak: 'break-all',
                          background: '#ffffff',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db'
              }}>
                          {selectedInvestment.transactionHash}
              </div>
            </div>
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Blockchain Network</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                          {selectedInvestment.blockchain || 'Ethereum'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Contract Address</div>
            <div style={{
                          fontSize: '12px', 
                          fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                          color: '#1f2937',
                          wordBreak: 'break-all',
                          background: '#ffffff',
                          padding: '8px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db'
                        }}>
                          {selectedInvestment.contractAddress || '0x...'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>Token Standard</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                          {selectedInvestment.tokenStandard || 'ERC-20'}
                        </div>
                      </div>
            </div>

                    <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <button
                        onClick={() => {
                          if (selectedInvestment.transactionHash) {
                            navigator.clipboard.writeText(selectedInvestment.transactionHash);
                            alert('Transaction hash copied to clipboard!');
                          }
                        }}
                style={{
                  padding: '8px 16px',
                          background: '#007bff',
                          color: '#fff',
                  border: 'none',
                          borderRadius: '6px',
                  cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        📋 Copy Transaction Hash
                      </button>
                      <button
                        onClick={() => {
                          if (selectedInvestment.contractAddress) {
                            navigator.clipboard.writeText(selectedInvestment.contractAddress);
                            alert('Contract address copied to clipboard!');
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                        }}
                      >
                        📋 Copy Contract Address
              </button>
            </div>
          </div>
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '24px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px dashed #d1d5db'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔗</div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Blockchain Data</h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                      This investment was made before blockchain integration was enabled.
                      Future investments will include detailed blockchain transaction information.
                    </p>
        </div>
      )}
              </div>
            </div>

            <div className="modal-footer">
              <div className="action-section">
                <button
                  onClick={() => {
                    setShowInvestmentDetailsModal(false);
                    setSelectedInvestment(null);
                  }}
                  className="action-btn secondary"
                >
                  Close
                </button>
    </div>
            </div>
          </div>
        </div>
      )}

      {/* Private Key Modal */}
      {showPrivateKeyModal && selectedWalletForPrivateKey && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}>
          <div style={{
            position: 'absolute',
            top: modalPosition?.top || '50%',
            left: modalPosition?.left || '50%',
            transform: modalPosition ? 'none' : 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxWidth: 'calc(100vw - 40px)',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            margin: '20px'
          }}>
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e9ecef'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#333'
              }}>
                🔑 Private Key
              </h3>
              <button
                onClick={handleClosePrivateKeyModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '4px',
                  borderRadius: '4px'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Wallet Info */}
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <strong>Wallet Address:</strong>
                <div style={{
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#6c757d',
                  wordBreak: 'break-all',
                  marginTop: '4px'
                }}>
                  {selectedWalletForPrivateKey.address}
                </div>
              </div>
              <div>
                <strong>Blockchain:</strong> {selectedWalletForPrivateKey.blockchain.toUpperCase()}
              </div>
            </div>

            {/* Private Key Display */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                color: '#333'
              }}>
                Private Key:
              </label>
              
              {isLoadingPrivateKey ? (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <div style={{ marginBottom: '8px' }}>⏳</div>
                  Decrypting private key...
                </div>
              ) : privateKeyError ? (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f8d7da',
                  borderRadius: '8px',
                  color: '#721c24',
                  border: '1px solid #f5c6cb'
                }}>
                  <div style={{ marginBottom: '8px' }}>❌</div>
                  {privateKeyError}
                </div>
              ) : privateKey ? (
                <div style={{
                  position: 'relative'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    wordBreak: 'break-all',
                    color: '#333',
                    minHeight: '60px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {privateKeyVisible ? privateKey : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                  </div>
                  
                  {/* Copy Button */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(privateKey);
                      // You could add a toast notification here
                    }}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#007bff',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    📋 Copy
                  </button>
                </div>
              ) : null}
            </div>

            {/* Security Warning */}
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px'
              }}>
                <div style={{ fontSize: '16px', marginTop: '2px' }}>⚠️</div>
                <div>
                  <strong style={{ color: '#856404' }}>Security Warning</strong>
                  <div style={{ fontSize: '14px', color: '#856404', marginTop: '4px' }}>
                    Never share your private key with anyone. Anyone with access to this key can control your wallet and steal your funds.
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleClosePrivateKeyModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Crypto; 